import countries from 'assets/jsons/Country.json';
import states from 'assets/jsons/State.json';
import moment from 'moment';
import { IUser } from 'oidc/user.redux';
import { IEditTimeOffForm } from '../components/EditTimeOffForm';

export const formatFormData = (data, disciplineOptions, specialtyOptions, travelCompanies) => {
  const selectedCountry = countries.countries.map(e => ({
    label: e.Description,
    value: e.CountryDialingCode,
    id: e.ID,
  }))
  .find(e => e.label == data?.country);

  const selectedState = states.states.map(e => ({ 
    id: e.ID, 
    label: e.Description, 
    value: e.StateNumber 
  }))
  .find(e => e.label == data?.state) ?? data?.state;

  const selectedDiscipline = disciplineOptions
    .map(e => {
      return { id: e.object.ID, label: e.label, value: e.object.ID };
    })
    .find(e => e.id == data?.disciplineId);

  const selectedTravelerCompany = travelCompanies
  .map(e => {
    return { id: e.value, label: e.name, value: e.name };
  })
  .find(e => e.id == data?.travelCompanyId);

  const formData: IEditWorkExperienceForm = {
    facilityName: data?.facilityName,
    country: selectedCountry,
    discipline: selectedDiscipline,
    city: data?.city,
    specialty: data?.specialtyId,
    state: (data?.countryId === 1 || data?.countryId === 2) ? selectedState : data?.state,
    zipCode: data?.zip,
    currentlyWorkingIsCurrentlyWorking: data?.currentlyEmployed ?? false,
    currentlyWorkingStartDate: moment(data?.startedOn).isValid() ? moment(data?.startedOn) : null,
    currentlyWorkingEndDate: moment(data?.endedOn).isValid() ? moment(data?.endedOn) : null,
    currentlyWorkingReasonForLeaving: data?.reasonForLeaving,
    numberOfBedsInFacility: data?.numberOfBedsInFacility,
    unitFloorDepartment: data?.unit,
    positionHeld: data?.position,
    travelCompany: selectedTravelerCompany,
    isSupervisor: data?.isSupervisor ?? false,
    isTeleTherapy: data?.isTeletherapy ?? false,
    isTeachingHospital: data?.isTeachingHospital ?? false,
    isLocalStaffingAgency: data?.staffingAgency ?? false,
    epnotes: typeof data?.equipmentProcedures == 'object' || data?.equipmentProcedures == "--" ? '' : data?.equipmentProcedures,
    workHistoryId: data?.workHistoryId,
    placementId: data?.placementID,
    position: data?.position,
    approvedReferenceCount: data?.approvedReferenceCount,
    timeStamp: data?.timeStamp,
    location: data?.location,
    previousEndedOn: data?.previousEndedOn,
  };
  return formData;
};

export const getWorkExperiencePayload = (
  data: IEditWorkExperienceForm,
  currentState,
  candidateId,
  brandId,
  user: IUser,
  specialties,
): IUpdateWorkExperience => {
  let payload: IUpdateWorkExperience = {
    workHistoryId: currentState?.workHistoryId,
    startedOn: moment(data?.currentlyWorkingStartDate).format('yyyy-MM-DDT00:00:00'),
    endedOn: data?.currentlyWorkingEndDate ? moment(data?.currentlyWorkingEndDate).format('yyyy-MM-DDT00:00:00') : null,
    workGap: 0,
    previousEndedOn: currentState?.previousEndedOn,
    yearsOfExperience: 0,
    monthsOfExperience: 0,
    discipline: '',
    disciplineAbbr: '',
    specialty: '',
    specialtyAbbr: '',
    position: data?.positionHeld,
    approvedReferenceCount: currentState?.approvedReferenceCount,
    facilityName: data?.facilityName,
    location: {
      address1: currentState?.location.address1,
      address2: currentState?.location.address2,
      city: typeof data?.city === 'string' ? data?.city : data?.city?.label,
      stateProvinceID: typeof data.state === 'string' ? data?.state : data.state?.id,
      state: typeof data.state === 'string' ? data.state : data.state?.id,
      country: data.country ? data.country?.label : null,
      zip: data?.zipCode,
      countryId: data.country ? data.country?.id : null,
      latitude: 0,
      longitude: 0,
    },
    unit: data?.unitFloorDepartment,
    travelCompany: data?.travelCompany?.label,
    staffingAgency: data?.isLocalStaffingAgency ? 'Y' : 'N',
    isTeachingHospital: data?.isTeachingHospital ? 'Y' : 'N',
    numberOfBedsInFacility: data?.numberOfBedsInFacility,
    currentlyEmployed: data?.currentlyWorkingIsCurrentlyWorking ? true : false,
    placementID: currentState?.placementId,
    reasonForLeaving: data?.currentlyWorkingReasonForLeaving,
    timeStamp: currentState?.timeStamp,
    ref: null,
    travelCompanyId: data?.travelCompany?.id,
    specialtyId: specialties?.find(x => x.label === data?.specialty)?.object?.SpecialtyID || null,
    disciplineId: data?.discipline?.id,
    otherDiscipline: '',
    otherSpecialty: '',
    equipmentProcedures: typeof data.epnotes === 'string' ? data.epnotes : '',
    isSupervisor: data?.isSupervisor,
    isTeletherapy: data?.isTeleTherapy,
    candidateId: candidateId,
    brandId: brandId,
    currentEmployeeId: user.userInfo?.employeeId || 8410,
    currentEmployeeName: user.userInfo?.firstName + ' ' + user.userInfo?.lastName,
    verifiedEMPVReferenceCount: currentState?.verifiedEMPVReferenceCount,
  };

  return payload;
};
export const getPayloadForTimeOff = (formData: IEditTimeOffForm, rowData, candidateId, brandId, user: IUser) => {
  let payload: IUpdateWorkExperience = {
    candidateId: candidateId,
    brandId: brandId,
    currentEmployeeId: user.userInfo?.employeeId || 8410,
    currentEmployeeName: user.userInfo?.firstName + ' ' + user.userInfo?.lastName,
    startedOn: formData.start,
    endedOn: formData.end,
    reasonForLeaving: formData.reason,
    workHistoryId: rowData?.workHistoryId,
    workGap: null,
    previousEndedOn: null,
    yearsOfExperience: 0,
    monthsOfExperience: 0,
    discipline: null,
    disciplineAbbr: null,
    specialty: null,
    specialtyAbbr: null,
    position: null,
    approvedReferenceCount: 0,
    facilityName: rowData?.facilityName,
    location: {
      countryId: null,
      country: null,
      state: null,
      stateProvinceID: null,
      address1: null,
      address2: null,
      city: null,
      zip: null,
      latitude: null,
      longitude: null,
    },
    unit: null,
    travelCompany: null,
    staffingAgency: null,
    isTeachingHospital: null,
    numberOfBedsInFacility: 0,
    currentlyEmployed: false,
    placementID: null,
    timeStamp: rowData?.timeStamp,
    ref: null,
    travelCompanyId: null,
    specialtyId: null,
    disciplineId: null,
    otherDiscipline: null,
    otherSpecialty: null,
    equipmentProcedures: null,
    isSupervisor: false,
    isTeletherapy: false,
    verifiedEMPVReferenceCount: rowData?.verifiedEMPVReferenceCount || 0,
  };
  return payload;
};

export interface IEditWorkExperienceForm {
  facilityName: string;
  country: any;
  discipline: any;
  city: any;
  specialty: any;
  state: any;
  zipCode: any;
  currentlyWorkingIsCurrentlyWorking: boolean;
  currentlyWorkingStartDate: any;
  currentlyWorkingEndDate: any;
  currentlyWorkingReasonForLeaving: string;
  numberOfBedsInFacility: number;
  unitFloorDepartment: string;
  positionHeld: string;
  travelCompany: any;
  isSupervisor: boolean;
  isTeleTherapy: boolean;
  isTeachingHospital: boolean;
  isLocalStaffingAgency: boolean;
  epnotes: string;
  workHistoryId: number;
  placementId: number;
  position: any;
  approvedReferenceCount: number;
  timeStamp: string;
  location: ILocation;
  previousEndedOn: any;
}

export interface IUpdateWorkExperience {
  workHistoryId: number;
  startedOn: string;
  endedOn: string;
  workGap: number;
  previousEndedOn: string;
  yearsOfExperience: number;
  monthsOfExperience: number;
  discipline: string;
  disciplineAbbr: string;
  specialty: string;
  specialtyAbbr: string;
  position: string;
  approvedReferenceCount: number;
  facilityName: string;
  location: ILocation;
  unit: string;
  travelCompany: string;
  staffingAgency: string;
  isTeachingHospital: string;
  numberOfBedsInFacility: number;
  currentlyEmployed: boolean;
  placementID: number;
  reasonForLeaving: string;
  timeStamp: string;
  ref: string;
  travelCompanyId: number;
  specialtyId: number;
  disciplineId: number;
  otherDiscipline: string;
  otherSpecialty: string;
  equipmentProcedures: string;
  isSupervisor: boolean;
  isTeletherapy: boolean;
  candidateId: number;
  brandId: number;
  currentEmployeeId: number;
  currentEmployeeName: string;
  verifiedEMPVReferenceCount: number;
}

export interface ILocation {
  address1: string;
  address2: string;
  city: string;
  stateProvinceID: string;
  state: string;
  zip: string;
  country: string;
  countryId: number;
  latitude: number;
  longitude: number;
}
